import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { CalendarCheck, Clock, Hourglass, Check, X as XIcon, Warning, CarProfile, Hash } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatLessonDate(dateStr) {
    if (!dateStr) return '';
    try {
        const d = new Date(`${dateStr}T00:00:00`);
        const today = new Date(); today.setHours(0, 0, 0, 0);
        const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);
        const target = new Date(d); target.setHours(0, 0, 0, 0);
        if (target.getTime() === today.getTime()) return 'Vandaag';
        if (target.getTime() === tomorrow.getTime()) return 'Morgen';
        return d.toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' });
    } catch {
        return dateStr;
    }
}

function getEndTime(time, durationMinutes) {
    if (!time) return '';
    try {
        const [h, m] = time.split(':').map(Number);
        const total = h * 60 + m + (durationMinutes || 60);
        const eh = String(Math.floor(total / 60) % 24).padStart(2, '0');
        const em = String(total % 60).padStart(2, '0');
        return `${eh}:${em}`;
    } catch { return ''; }
}

/**
 * Full-screen "memo" modal that pops up for students whenever they open the
 * app and have an unconfirmed lesson. Shows ALL details (date, time, duration,
 * notes) plus the 24-hour cancellation rule, and offers a big ✓ confirm / ✗
 * decline button.
 *
 * - Confirm  → POST /api/scheduled-lessons/{id}/confirm (instructor gets push)
 * - Decline  → dispatches `qr:open-cancel` so the existing bulk-cancel flow takes over
 */
export default function LessonConfirmModal({ open, lesson, onClose, studentName }) {
    const [busy, setBusy] = useState(false);

    if (!lesson) return null;

    const dateLabel = formatLessonDate(lesson.date);
    const endTime = getEndTime(lesson.time, lesson.duration_minutes);

    const confirm = async () => {
        if (busy) return;
        setBusy(true);
        try {
            await axios.post(`${API}/scheduled-lessons/${lesson.id}/confirm`, null, { withCredentials: true });
            toast.success('Les bevestigd ✅');
            onClose(true);
        } catch {
            toast.error('Bevestigen mislukt — probeer opnieuw');
        }
        setBusy(false);
    };

    const decline = () => {
        onClose(false);
        // Hand off to the global bulk-cancel modal which lists all upcoming lessons
        window.setTimeout(() => window.dispatchEvent(new CustomEvent('qr:open-cancel')), 150);
    };

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(false); }}>
            <DialogContent
                className="bg-[#1A202C] border-2 border-[#D4A537] text-white max-w-md max-h-[90vh] flex flex-col p-0 gap-0 overflow-hidden"
                data-testid="lesson-confirm-modal"
            >
                {/* Header */}
                <div className="px-5 py-4 bg-gradient-to-br from-[#D4A537]/20 via-[#D4A537]/5 to-transparent border-b border-[#4A5568] flex-shrink-0">
                    <div className="flex items-center gap-2">
                        <CalendarCheck size={22} weight="fill" className="text-[#D4A537]" />
                        <DialogTitle className="text-xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            Nieuwe rijles
                        </DialogTitle>
                    </div>
                    <DialogDescription className="text-sm text-gray-300 mt-1">
                        Bevestig of jouw les doorgaat
                    </DialogDescription>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
                    {/* Date pill */}
                    <div className="flex items-center justify-center">
                        <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#D4A537]/15 text-[#D4A537] border border-[#D4A537]/40 text-xs font-bold uppercase tracking-widest">
                            {dateLabel}
                        </span>
                    </div>

                    {/* Big time block */}
                    <div className="bg-[#2D3748] border border-[#4A5568] rounded-2xl p-4 text-center">
                        <div className="text-4xl font-bold text-white leading-none tabular-nums" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            {lesson.time || '--:--'}
                        </div>
                        <div className="text-xs text-gray-400 mt-1.5">tot {endTime || '--:--'} • {lesson.duration_minutes || 60} min</div>
                        <div className="text-xs text-gray-500 mt-0.5 capitalize">{lesson.date}</div>
                    </div>

                    {/* Detail rows */}
                    <div className="bg-[#2D3748] border border-[#4A5568] rounded-2xl overflow-hidden divide-y divide-[#4A5568]/60">
                        <DetailRow icon={CarProfile} label="Leerling" value={studentName} />
                        <DetailRow icon={Clock} label="Duur" value={`${lesson.duration_minutes || 60} minuten`} />
                        {lesson.notes && (
                            <DetailRow icon={Hash} label="Notitie" value={lesson.notes} multiline />
                        )}
                    </div>

                    {/* 24-hour rule — prominent */}
                    <div className="rounded-xl border-2 border-red-500/50 bg-red-500/10 p-3 flex items-start gap-2.5">
                        <div className="w-8 h-8 rounded-full bg-red-500/20 flex items-center justify-center flex-shrink-0">
                            <Warning size={18} weight="fill" className="text-red-400" />
                        </div>
                        <div className="text-sm text-red-200 leading-relaxed">
                            <div className="font-bold mb-0.5">Annuleer minimaal 24 uur vooraf</div>
                            Anders telt de les volgens je lespakket als een <strong>gereden les</strong>.
                        </div>
                    </div>

                    <div className="text-[11px] text-gray-500 leading-relaxed text-center pt-1">
                        <Hourglass size={11} weight="fill" className="inline -mt-0.5 mr-0.5" />
                        Bevestig zo snel mogelijk zodat je instructeur weet dat je komt.
                    </div>
                </div>

                {/* Footer with big ✓ and ✗ */}
                <div className="grid grid-cols-2 gap-2 p-3 border-t border-[#4A5568] bg-[#1A202C] flex-shrink-0">
                    <button
                        type="button"
                        onClick={decline}
                        disabled={busy}
                        className="inline-flex items-center justify-center gap-2 rounded-xl border-2 border-red-500/60 bg-red-500/10 hover:bg-red-500/20 text-red-300 font-bold py-3 transition-all active:scale-[0.97] disabled:opacity-50"
                        data-testid="confirm-modal-decline"
                        aria-label="Weigeren / annuleren"
                    >
                        <XIcon size={22} weight="bold" />
                        Weigeren
                    </button>
                    <button
                        type="button"
                        onClick={confirm}
                        disabled={busy}
                        className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-white font-bold py-3 transition-all active:scale-[0.97] disabled:opacity-50"
                        data-testid="confirm-modal-accept"
                        aria-label="Les bevestigen"
                    >
                        <Check size={22} weight="bold" />
                        {busy ? 'Bezig…' : 'Bevestigen'}
                    </button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function DetailRow({ icon: Icon, label, value, multiline }) {
    if (!value) return null;
    return (
        <div className="flex items-start gap-3 px-3 py-2.5">
            <div className="w-7 h-7 rounded-lg bg-[#1A202C] border border-[#4A5568] flex items-center justify-center flex-shrink-0 mt-0.5">
                <Icon size={14} className="text-[#D4A537]" weight="fill" />
            </div>
            <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-gray-500">{label}</div>
                <div className={`text-sm text-white ${multiline ? 'whitespace-pre-wrap' : 'truncate'}`} style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {value}
                </div>
            </div>
        </div>
    );
}
